<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { NuxtLink } from '#components';
import { type ReserveType } from '@utils/itinerary';

export type TabItemVM = {
    id: string;
    reserveType: ReserveType;
    label: string;
    isSelected: boolean;
    href: string;
    desktopIcon: string;
    mobileIcon: IconDefinition;
    tag?: string;
    loading?: boolean;
    disabled?: boolean;
};

const { item } = defineProps<{
    item: TabItemVM;
}>();
const emit = defineEmits<{
    click: [event: MouseEvent];
}>();

const { isMobile } = useSize();
const root = ref<HTMLElement>();
const onClick = (event: MouseEvent) => {
    emit('click', event);
    if (isMobile.value) {
        root.value?.scrollIntoView({
            block: 'center',
        });
    }
};
</script>

<template>
    <li
        ref="root"
        class="tab-item-container"
        :class="{
            selected: item.isSelected,
            'loading-skeleton': item.loading,
        }"
        @click="onClick"
    >
        <NuxtLink
            :id="item.id"
            class="tab"
            :class="{ 'loading-skeleton': item.loading }"
            :to="item.href"
            :event="item.loading ? '' : 'click'"
        >
            <span v-if="item.tag" class="tab-tag">
                {{ item.tag }}
                <svg
                    class="tag-angle"
                    width="8"
                    height="9"
                    viewBox="0 0 8 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M8 9L0 0H8V9Z" />
                </svg>
            </span>
            <svg
                class="desktop tab-icon"
                :class="{ 'loading-skeleton': item.loading }"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                v-html="item.desktopIcon"
            />
            <mr-icon
                class="mobile tab-icon"
                :icon="item.mobileIcon"
                size="small"
                :color="item.isSelected ? 'white' : 'var(--blue-300)'"
            />
            <div class="tab-label">
                {{ item.label }}
            </div>
        </NuxtLink>
    </li>
</template>

<style lang="scss" scoped>
.tab-item-container {
    margin-left: 8px;
    z-index: 30;

    &.selected {
        .tab {
            background-color: usePalette($p-yellow, $p-300);
            animation: change-background 200ms ease-in;

            .tab-tag {
                background: usePalette($p-yellow, $p-700);
                color: usePalette($p-yellow, $p-300);
                .tag-angle {
                    fill: usePalette($p-yellow, $p-700);
                }
            }
            .tab-icon {
                --color: #{usePalette($p-yellow, $p-700)};
                fill: usePalette($p-yellow, $p-700);
            }
            .tab-label {
                @include tg-l($w7, usePalette($p-yellow, $p-700));
            }
        }
    }
}

@keyframes change-background {
    0% {
        background-color: white;
    }
    25% {
        background-color: usePalette($p-yellow, $p-100);
    }
    50% {
        background-color: usePalette($p-yellow, $p-200);
    }
    100% {
        background-color: usePalette($p-yellow, $p-300);
    }
}

.tab {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    width: 104px;
    height: 104px;
    cursor: pointer;
    user-select: none;
    background: white;
    border-radius: 16px;
    @include shadow(1);

    &:hover:not(.loading-skeleton) {
        @include shadow(2);
        text-decoration-line: none;
    }

    &:active {
        text-decoration-line: none;
        color: usePalette();
    }

    .tab-icon {
        --color: #{usePalette()};
        // default is fill, disable it explicitly when needed
        fill: usePalette();
        &.loading-skeleton {
            --color: #{usePalette($p-blue, $p-100)};
            fill: usePalette($p-blue, $p-100) !important;
        }
        &.mobile {
            @include desktop-only {
                display: none;
            }
        }
        &.desktop {
            @include mobile-only {
                display: none;
            }
        }
    }

    .tab-tag {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        top: 8px;
        left: -7px;
        width: 40px;
        height: 18px;
        background: usePalette($p-yellow, $p-300);
        border-radius: 4px 4px 4px 0;
        padding: 2px 0;
        // text
        @include tg-xs($w7, usePalette($p-yellow, $p-700));
        line-height: unset;
        .tag-angle {
            position: absolute;
            left: 0;
            top: 100%;
            fill: usePalette($p-yellow, $p-300);
        }
    }

    .tab-label {
        min-height: 28px;
        @include tg-m($w7, usePalette());
    }
}

@media (max-width: $app-mobile-max-width) {
    .tab-item-container {
        flex: 1;
        position: relative;
        height: 68px;
        width: auto;
        margin: 0;
        padding-inline: 8px;
        transition: all 200ms;
        * {
            transition: all 200ms;
        }
        &.selected {
            .tab {
                animation: unset;
                background: transparent;

                .tab-tag {
                    background: usePalette($p-yellow, $p-300);
                    color: usePalette($p-yellow, $p-700);
                }

                .tab-label {
                    color: white;
                }
            }
        }
    }
    .tab {
        flex-shrink: 0;
        height: 68px;
        min-width: 35px;
        width: auto;
        border-radius: 0 !important;
        background-color: transparent;
        color: usePalette();
        padding-left: 0;
        padding-right: 0;
        @include shadow(0);
        &:hover {
            @include shadow(0);
        }
        .tab-icon {
            --color: #{usePalette($p-blue, $p-300)};
            fill: usePalette($p-blue, $p-300);
            margin-left: 0;
        }
        .tab-tag {
            height: 12px;
            width: 64px;
            top: unset;
            right: unset;
            bottom: 0;
            left: 50%;
            border-radius: 12px 12px 0 0;
            transform: translateX(-50%);
            // text
            @include tg-xs($w7, usePalette($p-yellow, $p-700));
            line-height: unset;
            .tag-angle {
                display: none;
            }
        }
        .tab-label {
            @include tg-m($w7, white);
            white-space: nowrap;
            text-align: center;

            color: usePalette($p-blue, $p-300);
        }
    }
}
</style>
